import type { ChartFieldType, ChartType, ChartTypeMeta } from '../../models/chart.types'

export interface RendererRoleCapability {
  key: string
  fieldType: ChartFieldType
  required: boolean
  multiple: boolean
}

export interface RendererCapability {
  type: ChartType
  nameKey: ChartTypeMeta['nameKey']
  minDimensions: number
  maxDimensions: number
  minMetrics: number
  maxMetrics: number
  requiresTimeDimension: boolean
  requiresComparison: boolean
  defaultLimit: number | null
  roles: readonly RendererRoleCapability[]
}

interface CapabilityOptions {
  dimensions: readonly [min: number, max: number]
  metrics: readonly [min: number, max: number]
  time?: boolean
  comparison?: boolean
  limit?: number | null
  roles: readonly RendererRoleCapability[]
}

const CHART_TYPE_NAME_KEYS: Record<ChartType, ChartTypeMeta['nameKey']> = {
  line: 'reports.chartTypes.line',
  area: 'reports.chartTypes.area',
  stacked_area: 'reports.chartTypes.stackedArea',
  multi_line: 'reports.chartTypes.multiLine',
  compare_column: 'reports.chartTypes.compareColumn',
  compare_bar: 'reports.chartTypes.compareBar',
  column: 'reports.chartTypes.column',
  bar: 'reports.chartTypes.bar',
  stacked_column: 'reports.chartTypes.stackedColumn',
  stacked_bar: 'reports.chartTypes.stackedBar',
  pie: 'reports.chartTypes.pie',
  multi_pie: 'reports.chartTypes.multiPie',
  donut: 'reports.chartTypes.donut',
  scatter: 'reports.chartTypes.scatter',
  bubble: 'reports.chartTypes.bubble',
  heatmap: 'reports.chartTypes.heatmap',
  treemap: 'reports.chartTypes.treemap',
  sunburst: 'reports.chartTypes.sunburst',
  funnel: 'reports.chartTypes.funnel',
  waterfall: 'reports.chartTypes.waterfall',
  radar: 'reports.chartTypes.radar',
  gauge: 'reports.chartTypes.gauge',
  sankey: 'reports.chartTypes.sankey',
  packed_bubble: 'reports.chartTypes.packedBubble',
  lollipop: 'reports.chartTypes.lollipop',
  combination: 'reports.chartTypes.combination',
  multi_metric: 'reports.chartTypes.multiMetric',
  dumbbell: 'reports.chartTypes.dumbbell',
  bullet: 'reports.chartTypes.bullet',
  diverging_bar: 'reports.chartTypes.divergingBar',
}

function capability(type: ChartType, options: CapabilityOptions): RendererCapability {
  return {
    type,
    nameKey: CHART_TYPE_NAME_KEYS[type],
    minDimensions: options.dimensions[0],
    maxDimensions: options.dimensions[1],
    minMetrics: options.metrics[0],
    maxMetrics: options.metrics[1],
    requiresTimeDimension: options.time === true,
    requiresComparison: options.comparison === true,
    defaultLimit: options.limit ?? null,
    roles: options.roles,
  }
}

function role(
  key: string,
  fieldType: ChartFieldType,
  required = true,
  multiple = false,
): RendererRoleCapability {
  return { key, fieldType, required, multiple }
}

const TIME = role('time_dimension', 'time_dimension')
const OPTIONAL_TIME = role('time_dimension', 'time_dimension', false)
const DIMENSION = role('dimension', 'dimension')
const OPTIONAL_DIMENSION = role('dimension', 'dimension', false)
const DIMENSIONS = role('dimensions', 'dimension', true, true)
const OPTIONAL_DIMENSIONS = role('dimensions', 'dimension', false, true)
const METRIC = role('metric', 'metric')
const METRICS = role('metrics', 'metric', true, true)

/** Closed set of implemented builders plus their local defaults. When the backend catalog lists a
 *  type, every constraint it sends wins; these values fill only the fields it leaves out, and stand
 *  in for the whole catalog while it is unavailable. */
export const chartRendererRegistry: readonly RendererCapability[] = [
  capability('line', {
    dimensions: [1, 2],
    metrics: [1, 10],
    time: true,
    roles: [TIME, OPTIONAL_DIMENSIONS, METRICS],
  }),
  capability('area', {
    dimensions: [1, 2],
    metrics: [1, 10],
    time: true,
    roles: [TIME, OPTIONAL_DIMENSIONS, METRICS],
  }),
  capability('stacked_area', {
    dimensions: [1, 2],
    metrics: [1, 10],
    time: true,
    roles: [TIME, OPTIONAL_DIMENSIONS, METRICS],
  }),
  capability('multi_line', {
    dimensions: [1, 1],
    metrics: [1, 1],
    time: true,
    limit: 10,
    roles: [TIME, DIMENSION, METRIC],
  }),
  capability('compare_column', {
    dimensions: [1, 1],
    metrics: [1, 1],
    comparison: true,
    limit: 10,
    roles: [DIMENSION, METRICS],
  }),
  capability('compare_bar', {
    dimensions: [1, 1],
    metrics: [1, 5],
    comparison: true,
    limit: 10,
    roles: [DIMENSION, METRICS],
  }),
  capability('column', {
    dimensions: [1, 2],
    metrics: [1, 10],
    limit: 10,
    roles: [OPTIONAL_TIME, DIMENSIONS, METRICS],
  }),
  capability('bar', {
    dimensions: [1, 2],
    metrics: [1, 4],
    limit: 10,
    roles: [OPTIONAL_TIME, DIMENSIONS, METRICS, role('line_metric', 'metric', false)],
  }),
  capability('stacked_column', {
    dimensions: [1, 2],
    metrics: [1, 10],
    roles: [OPTIONAL_TIME, DIMENSIONS, METRICS],
  }),
  capability('stacked_bar', {
    dimensions: [1, 2],
    metrics: [1, 10],
    roles: [OPTIONAL_TIME, DIMENSIONS, METRICS],
  }),
  capability('pie', {
    dimensions: [1, 1],
    metrics: [1, 2],
    limit: 10,
    roles: [DIMENSION, METRICS],
  }),
  capability('multi_pie', {
    dimensions: [1, 1],
    metrics: [1, 4],
    limit: 10,
    roles: [DIMENSION, METRICS],
  }),
  capability('donut', {
    dimensions: [1, 1],
    metrics: [1, 2],
    limit: 10,
    roles: [DIMENSION, METRICS],
  }),
  capability('scatter', {
    dimensions: [0, 1],
    metrics: [2, 2],
    roles: [role('metric_x', 'metric'), role('metric_y', 'metric'), OPTIONAL_DIMENSION],
  }),
  capability('bubble', {
    dimensions: [1, 1],
    metrics: [3, 3],
    roles: [
      role('metric_x', 'metric'),
      role('metric_y', 'metric'),
      role('metric_size', 'metric'),
      DIMENSION,
    ],
  }),
  capability('heatmap', {
    dimensions: [2, 2],
    metrics: [1, 1],
    roles: [role('dimension_x', 'dimension'), role('dimension_y', 'dimension'), METRIC],
  }),
  capability('treemap', {
    dimensions: [1, 5],
    metrics: [1, 1],
    limit: 50,
    roles: [DIMENSIONS, METRIC],
  }),
  capability('sunburst', { dimensions: [1, 5], metrics: [1, 1], roles: [DIMENSIONS, METRIC] }),
  capability('funnel', { dimensions: [1, 1], metrics: [1, 1], roles: [DIMENSION, METRIC] }),
  capability('waterfall', {
    dimensions: [1, 1],
    metrics: [1, 1],
    roles: [DIMENSION, METRIC],
  }),
  capability('radar', { dimensions: [1, 1], metrics: [1, 10], roles: [DIMENSION, METRICS] }),
  // A gauge builder is intentionally scalar: without this cap the degraded catalog would fetch
  // an arbitrary row set and the renderer would have to guess which point represents the KPI.
  capability('gauge', { dimensions: [0, 0], metrics: [1, 1], limit: 1, roles: [METRIC] }),
  capability('sankey', {
    dimensions: [2, 2],
    metrics: [1, 1],
    limit: 50,
    roles: [role('dimension_source', 'dimension'), role('dimension_target', 'dimension'), METRIC],
  }),
  capability('packed_bubble', { dimensions: [1, 2], metrics: [1, 1], roles: [DIMENSIONS, METRIC] }),
  capability('lollipop', { dimensions: [1, 1], metrics: [1, 1], roles: [DIMENSION, METRIC] }),
  capability('combination', {
    dimensions: [1, 2],
    metrics: [2, 10],
    roles: [OPTIONAL_TIME, DIMENSIONS, METRICS],
  }),
  capability('multi_metric', {
    dimensions: [1, 1],
    metrics: [1, 5],
    limit: 250,
    roles: [DIMENSIONS, METRICS],
  }),
  capability('dumbbell', {
    dimensions: [1, 1],
    metrics: [1, 1],
    comparison: true,
    limit: 20,
    roles: [DIMENSION, METRIC],
  }),
  capability('bullet', {
    dimensions: [0, 1],
    metrics: [1, 1],
    comparison: true,
    roles: [OPTIONAL_DIMENSION, METRIC, role('target', 'target')],
  }),
  capability('diverging_bar', {
    dimensions: [2, 2],
    metrics: [1, 1],
    limit: 20,
    roles: [DIMENSION, role('breakdown', 'dimension'), METRIC],
  }),
]
